import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import { routePaths } from "../../routePaths";
import { getDeploymentLogsPage, getDeployments } from "../../services/pdmApi.service";
import type { Deployment, DeploymentLogItem } from "../../types/pdm.types";

const LOGS_PAGE_SIZE = 20;

function formatTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

function levelColor(level: string) {
  switch (level) {
    case "error":
      return "error" as const;
    case "warn":
      return "warning" as const;
    case "info":
      return "info" as const;
    default:
      return "default" as const;
  }
}

function deploymentStatusColor(status: Deployment["status"]) {
  switch (status) {
    case "success":
      return "success" as const;
    case "pending":
      return "warning" as const;
    case "in_progress":
      return "info" as const;
    case "failed":
      return "error" as const;
    default:
      return "default" as const;
  }
}

export function DeploymentLog({ deployment }: { deployment: Deployment }) {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * LOGS_PAGE_SIZE;

  const logsQuery = useQuery({
    queryKey: ["deploymentLogs", deployment.id, offset],
    queryFn: () =>
      getDeploymentLogsPage(deployment.id, { offset, limit: LOGS_PAGE_SIZE }),
    enabled: Boolean(deployment.id),
    refetchInterval: deployment.status === "in_progress" ? 10_000 : false,
    placeholderData: (prev) => prev,
  });

  const items = logsQuery.data?.items ?? [];
  const total = logsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / LOGS_PAGE_SIZE));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{ alignItems: { xs: "stretch", sm: "center" }, gap: 1, mb: 1 }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          Logs
        </Typography>
        <Chip
          size="small"
          label={deployment.status.replace(/_/g, " ")}
          color={deploymentStatusColor(deployment.status)}
          variant="outlined"
        />
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          bgcolor: "#0b0f14",
          color: "#e6edf3",
          borderColor: "rgba(230, 237, 243, 0.16)",
          p: 1.5,
          fontFamily: '"Roboto Mono", Consolas, monospace',
          fontSize: "0.85rem",
          lineHeight: 1.35,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {logsQuery.isLoading && (
          <Typography sx={{ color: "rgba(230, 237, 243, 0.65)" }}>
            Loading logs…
          </Typography>
        )}
        {logsQuery.isError && (
          <Typography sx={{ color: "error.light" }}>
            Unable to load logs.
          </Typography>
        )}

        {!logsQuery.isLoading && !logsQuery.isError && !items.length && (
          <Typography sx={{ color: "rgba(230, 237, 243, 0.65)" }}>
            No log entries yet.
          </Typography>
        )}

        {!logsQuery.isLoading &&
          !logsQuery.isError &&
          items.map((log: DeploymentLogItem) => (
            <Box
              key={log.id}
              sx={{
                py: 0.5,
                borderBottom: "1px solid",
                borderBottomColor: "rgba(230, 237, 243, 0.08)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                sx={{ gap: 1, alignItems: { xs: "flex-start", sm: "center" } }}
              >
                <Typography
                  sx={{
                    color: "rgba(230, 237, 243, 0.55)",
                    minWidth: 180,
                  }}
                >
                  {formatTimestamp(log.timestamp)}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={log.level}
                  color={levelColor(log.level)}
                  sx={{ height: 22, fontFamily: "inherit" }}
                />
                <Typography
                  sx={{ color: "rgba(230, 237, 243, 0.55)", minWidth: 70 }}
                >
                  step {log.step}
                </Typography>
                <Typography sx={{ flex: 1 }}>{log.message}</Typography>
              </Stack>
            </Box>
          ))}
      </Paper>

      <Divider sx={{ my: 1.5 }} />

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          gap: 2,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Typography color="text.secondary" sx={{ alignSelf: "center" }}>
          {total ? `${total} total` : "—"}
        </Typography>
        <Pagination
          count={pageCount}
          page={Math.min(page, pageCount)}
          onChange={(_, next) => setPage(next)}
          color="primary"
          siblingCount={0}
          boundaryCount={1}
        />
      </Stack>
    </Box>
  );
}

export default function DeploymentLogsView() {
  const { id } = useParams<{ id: string }>();
  const deploymentId = id ?? "";

  const deploymentQuery = useQuery({
    queryKey: ["deploymentsLookup", deploymentId],
    queryFn: async () => {
      const { items } = await getDeployments({ offset: 0, limit: 100 });
      return items.find((d) => d.id === deploymentId) ?? null;
    },
    enabled: Boolean(deploymentId),
  });

  const deployment: Deployment | null = deploymentQuery.data ?? null;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: "brand.main" }}>
        Deployment logs
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Deployment ID:{" "}
        <Typography component="span" sx={{ fontFamily: "monospace" }}>
          {deploymentId || "—"}
        </Typography>
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Button component={RouterLink} to={routePaths.deployments} variant="outlined">
          Back to deployments
        </Button>
      </Box>

      {deploymentQuery.isLoading && (
        <Typography color="text.secondary">Loading deployment…</Typography>
      )}
      {deploymentQuery.isError && (
        <Typography color="error">Unable to load deployment.</Typography>
      )}
      {!deploymentQuery.isLoading && !deploymentQuery.isError && !deployment && (
        <Typography color="text.secondary">
          Deployment not found. Showing logs without live polling.
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <DeploymentLog
          deployment={
            deployment ?? {
              id: deploymentId,
              name: deploymentId,
              status: "success",
              network: "",
              deploymentTemplateId: "",
              createdAt: new Date(0).toISOString(),
            }
          }
        />
      </Box>
    </Box>
  );
}
